/**
 * Call context is the raw material an SDR captured from an actual conversation
 * with the prospect: either a pasted transcript or their own written summary.
 *
 * It is persisted against the account + contact so every subsequent email,
 * follow-up and sequence step can draw from the same call instead of the SDR
 * re-pasting it each time.
 */
export const CALL_CONTEXT_SOURCES = {
  TRANSCRIPT: "transcript",
  SUMMARY: "summary",
} as const;

export type CallContextSourceT =
  (typeof CALL_CONTEXT_SOURCES)[keyof typeof CALL_CONTEXT_SOURCES];

export const CALL_CONTEXT_SOURCE_LABELS: Record<CallContextSourceT, string> = {
  [CALL_CONTEXT_SOURCES.TRANSCRIPT]: "Call transcript",
  [CALL_CONTEXT_SOURCES.SUMMARY]: "Call summary",
};

/**
 * Generous ceiling: a 30 minute discovery call transcribes to roughly 25k
 * characters. Anything beyond this is almost certainly a paste accident.
 */
export const CALL_CONTEXT_MAX_LENGTH = 50000;

/**
 * Below this there is not enough signal to write an email from, so the
 * generator falls back to account summary + elevator pitch.
 */
export const CALL_CONTEXT_MIN_LENGTH = 80;

export interface CallContextI {
  id: string;
  source: CallContextSourceT;
  content: string;
  created_at: string;
  created_by_name?: string;
}

export interface SaveCallContextRequestParamsI {
  contact_id: string;
  source: CallContextSourceT;
  content: string;
}

export interface SaveCallContextResponseI {
  call_context: CallContextI;
}

export interface GetCallContextResponseI {
  /** Null when the account has no captured call context yet. */
  call_context: CallContextI | null;
}
